import { ReactElement, useEffect, useMemo, useState } from 'react'
import Wizard from '../../Wizard'
import {
  ADD_PROMPT,
  AddToListStage,
  useAddToListStore,
} from 'src/store/lists/AddToListStore'
import Breadcrumb from '../../Breadcrumb/Breadcrumb'
import { useListsStore } from 'src/store/lists/ListsStore'
import { I18n, Translate } from 'src/i18n'
import styled from 'styled-components'
import Table from './Table'
import { observer } from 'mobx-react-lite'
import { columns } from './Table/data'
import Messages from './Messages'
import Pager from 'src/ui-components/table/pagination'
import { useLocation } from 'react-router-dom'
import history from 'src/helpers/history'
import { Part } from '../../Common/interfaces'
import {
  getImageUrl,
  getPartImages,
  PartIdentifier,
} from 'src/helpers/partImageHelpers'

const Review = observer((): ReactElement => {
  const listStore = useListsStore()
  const addToListStore = useAddToListStore()
  const categoryLabel = listStore.getSelectedCategoryLabel()
  const listLabel = listStore.getSelectedListLabel()
  const listId = listStore.getSelectedListId()
  const partsBin = addToListStore.getPartsBin()
  const partsBinId = addToListStore.getValidatedListBinId()
  const isAddToListEnabled = addToListStore.isAddToListEnabled()
  const baseUrl = `?partsBinId=${partsBinId}`
  const [offset, setOffset] = useState<number>()
  const [limit, setLimit] = useState<number>()
  const [partsSlice, setPartsSlice] = useState<Part[]>([])
  const end = Math.min(partsBin?.parts?.length, offset + limit)
  useEffect(() => {
    const parts = partsBin?.parts?.slice(offset, end) || []
    if (parts.length === 0) {
      setPartsSlice([])
      return
    }
    const partDetail: PartIdentifier[] = parts.map((part) => ({
      lineCode: part.lineCode,
      partNumber: part.partNum,
    }))
    getPartImages({ parts: partDetail }).then((resp) => {
      setPartsSlice(
        parts.map((part) => ({
          ...part,
          imgUrl: getImageUrl({
            linecode: part.lineCode,
            partnumber: part.partNum,
            partImages: resp,
          }),
        }))
      )
    })
  }, [end, offset, partsBin, partsBin.parts])

  const location = useLocation()

  const showStages = addToListStore.getSelectedPrompt() !== ADD_PROMPT.NONE

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const pOffset = params.get('offset')
    const pLimit = params.get('limit')

    setOffset(Number(pOffset ?? 0))
    setLimit(Number(pLimit ?? 10))
  }, [location])

  const breadCrumbItems = useMemo(
    () => [{ text: categoryLabel }, { text: listLabel }],

    [categoryLabel, listLabel]
  )

  const addPartsToList = async (): Promise<void> => {
    listStore.clearAllSelections()
    window.scrollTo({ top: 0, behavior: 'smooth' })
    listStore.resetSelectedListGroups()
    await addToListStore.addPartsToList(listId, partsBinId)
    addToListStore.setStage(AddToListStage.CHOOSE_METHOD)
  }

  const goBack = (): void => {
    addToListStore.setError(null)
    const selectedPrompt = addToListStore.getSelectedPrompt()

    const newStage =
      selectedPrompt === ADD_PROMPT.NONE
        ? AddToListStage.CHOOSE_METHOD
        : addToListStore.getStage() - 2

    addToListStore.setStage(newStage)
  }

  const addToListLink = {
    text: 'confirm',
    action: addPartsToList,
    enabled: isAddToListEnabled,
  }
  const goBackLink = { text: 'back', action: goBack }

  const handlePageChange = async (page: number, currentPageSize: number) => {
    const start = (page - 1) * currentPageSize // -1 because the API starts counting from 0

    const end = start + currentPageSize
    const accumulatedPartsLength = partsBin?.parts?.length
    const loadOtherFragment =
      accumulatedPartsLength < end &&
      accumulatedPartsLength < partsBin?.totalParts

    if (loadOtherFragment) {
      const nextPartsBinFragment = await addToListStore.getValidatedItems(
        partsBinId,
        start,
        currentPageSize
      )

      const partsToAcummulate = nextPartsBinFragment?.parts?.filter(
        (p1) => !partsBin?.parts?.find((p2) => p1.seqno === p2.seqno)
      )
      const accummulatedParts = [
        ...(partsBin?.parts || []),
        ...(partsToAcummulate || []),
      ]
      nextPartsBinFragment.parts = accummulatedParts

      addToListStore.setPartsBin(nextPartsBinFragment)
    }

    window.scrollTo({ top: 0, behavior: 'smooth' })
    history.push(`${baseUrl}&offset=${start}&limit=${currentPageSize}`)
  }

  const getPaginationInfo = () => {
    return {
      totalItems: partsBin.totalParts,
      itemsPerPageScale: 10,
      onPageChange: handlePageChange,
      currentPageSize: 10,
      paginationTitle: Translate('products'),
    }
  }

  const paginationInfo = getPaginationInfo()

  return (
    <Wizard
      title="reviewItems"
      goNext={addToListLink}
      goBack={goBackLink}
      showStages={showStages}
    >
      <Container>
        <Breadcrumb items={breadCrumbItems} />
        <Instructions>
          <I18n name="reviewTheItemsYouWishToAddToList" />.
        </Instructions>
        <Messages validatedParts={partsBin?.parts} />
        <Table columns={columns} rows={partsSlice} />
        <PagerWrapper>
          <Pager {...paginationInfo} />
        </PagerWrapper>
      </Container>
    </Wizard>
  )
})

const Container = styled.div``

const Instructions = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  margin-bottom: 1rem;
`

const PagerWrapper = styled.div`
  padding: 16px 32px 16px 32px;
`

export default Review
